import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyStudentPage: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    // 履歴があるかチェックして、なければチャットページに戻る
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/chat');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            戻る
          </Button>
        </Box>

        <Paper
          elevation={8}
          sx={{
            p: { xs: 3, sm: 4, md: 6 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Title */}
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 4,
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            学生向けプライバシーポリシー
          </Typography>

          {/* Company Information */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              会社名：株式会社Gerbera
            </Typography>
            <Typography variant="body1" color="text.secondary">
              代表取締役：𠮷岡 歩真
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Introduction */}
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            株式会社Gerbera（以下「弊社」といいます）は、学生ユーザーの個人情報保護に関する法令を遵守し、その取扱い及び保護等について、個人情報保護法に基づき、以下のとおりプライバシーポリシー（以下「本ポリシー」といいます）を定めます。
          </Typography>

          {/* Privacy Policy Items */}
          <List sx={{ mb: 4 }}>
            {/* 第1条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第1条（個人情報の定義）
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    本ポリシーにおいて「個人情報」とは、個人情報保護法第2条第1項に定める個人情報、すなわち、生存する個人に関する情報であって、当該情報に含まれる氏名、生年月日その他の記述等により特定の個人を識別することができるもの（他の情報と容易に照合することができ、それにより特定の個人を識別することができることとなるものを含む）をいいます。
                  </Typography>
                }
              />
            </ListItem>

            {/* 第2条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第2条（収集する個人情報の項目）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      弊社は、本サービスにおいて、以下の個人情報を収集いたします。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （1）氏名、メールアドレス、電話番号
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （2）生年月日、性別、年齢
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （3）大学名、学部・学科、卒業年度、学籍番号
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （4）住所、居住地域
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （5）職歴、資格、スキル、自己PR等のキャリア情報
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （6）本サービスの利用履歴、アクセスログ、Cookie情報
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （7）AIチャット機能における会話履歴及び学習履歴
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, pl: 2 }}>
                      （8）その他、本サービスの提供に必要な情報
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第3条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第3条（個人情報の利用目的）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      弊社は、収集した個人情報を以下の目的で利用いたします。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （1）本サービスの提供、運営、維持、改善のため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （2）本サービスに関する登録、本人確認、ログイン認証のため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （3）利用企業とのマッチング、オファー送信のため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （4）AIチャット機能の提供及び学習支援のため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （5）利用者へのお知らせ、連絡、カスタマーサポートのため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （6）利用状況の分析、統計データの作成のため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （7）本サービスに関する規約違反、不正利用の調査・対応のため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （8）法令に基づく対応のため
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, pl: 2 }}>
                      （9）その他、上記利用目的に付随する目的のため
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第4条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第4条（個人情報の第三者提供）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      １　弊社は、以下の場合を除き、あらかじめ学生ユーザーの同意を得ることなく、第三者に個人情報を提供いたしません。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （1）法令に基づく場合
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （2）人の生命、身体又は財産の保護のために必要がある場合であって、本人の同意を得ることが困難であるとき
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （3）公衆衛生の向上又は児童の健全な育成の推進のために特に必要がある場合であって、本人の同意を得ることが困難であるとき
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （4）国の機関若しくは地方公共団体又はその委託を受けた者が法令の定める事務を遂行することに対して協力する必要がある場合であって、本人の同意を得ることにより当該事務の遂行に支障を及ぼすおそれがあるとき
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      ２　弊社は、利用者のマイページ情報を、利用企業に対して提供いたします。この点について、利用者は、利用規約への同意をもって、あらかじめ同意しているものとします。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      ３　弊社は、本サービスの提供に必要な範囲で、業務委託先に個人情報を提供する場合があります。この場合、弊社は、業務委託先との間で秘密保持契約を締結する等、適切な監督を行います。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第5条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第5条（個人情報の安全管理措置）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      弊社は、個人情報の紛失、破壊、改ざん及び漏洩等のリスクに対して、合理的な安全対策を実施し、個人情報の安全管理に努めます。具体的には、以下の措置を講じます。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （1）個人情報へのアクセス制御及びアクセス権限の管理
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （2）個人情報を取り扱う機器、電子媒体等の盗難又は紛失等を防止するための措置
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （3）外部からの不正アクセス又は不正ソフトウェアから保護する仕組みの導入
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, pl: 2 }}>
                      （4）従業員に対する個人情報保護に関する教育・研修の実施
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第6条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第6条（個人情報の開示・訂正・削除）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      １　学生ユーザーは、弊社に対し、個人情報保護法の定めるところにより、自己の個人情報の開示を請求することができます。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      ２　前項の請求により開示された個人情報の内容に誤りがある場合、学生ユーザーは、弊社に対し、当該個人情報の訂正、追加又は削除（以下「訂正等」といいます）を請求することができます。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      ３　弊社は、前項の請求を受けた場合、遅滞なく必要な調査を行い、その結果に基づき、個人情報の訂正等を行い、その旨を当該学生ユーザーに通知します。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      ４　学生ユーザーは、弊社に対し、個人情報保護法の定めるところにより、自己の個人情報の利用停止又は消去を請求することができます。弊社は、当該請求に理由があると判断した場合、遅滞なく利用停止又は消去を行い、その旨を当該学生ユーザーに通知します。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第7条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第7条（Cookie及びアクセス解析ツールの使用）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      １　弊社は、本サービスの利便性向上及びサービス改善のため、Cookie及びこれに類する技術を使用することがあります。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      ２　弊社は、本サービスの利用状況を把握するため、Google Analytics等のアクセス解析ツールを使用することがあります。これらのツールは、Cookieを使用して利用者の情報を収集します。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      ３　学生ユーザーは、ブラウザの設定によりCookieの受け取りを拒否することができますが、その場合、本サービスの一部機能が利用できない場合があります。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第8条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第8条（個人情報の保有期間）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      １　弊社は、個人情報を利用目的の達成に必要な期間に限り保有します。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      ２　弊社は、マッチング率の向上等のサービス向上のための追跡調査等を目的として、当該利用者の個人情報を保有し、利用できるものとします。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      ３　前項の期間経過後は、弊社は個人情報を個人が特定できない形式（統計情報又は匿名加工情報）に加工して保有します。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第9条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第9条（統計情報及び匿名加工情報の利用）
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    弊社は、学生ユーザーの個人情報を個人が特定できない形式に加工した統計情報又は匿名加工情報を作成し、本サービスの改善、マーケティング、調査研究等の目的で利用又は第三者に提供することがあります。これらの情報は個人を特定するものではありません。
                  </Typography>
                }
              />
            </ListItem>

            {/* 第10条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第１０条（マッチング等分析における守秘義務）
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    弊社が利用企業からの委託によってマッチング等分析を行った場合、その分析結果については、当該利用企業の営業秘密であり、かつ、利用企業の従業員の個人情報を含むものであるため、当該利用企業との関係で守秘義務の対象となっており弊社に開示権限がありません。そのため、学生ユーザーからの開示請求には応じることができません。
                  </Typography>
                }
              />
            </ListItem>

            {/* 第11条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第１１条（個人情報取扱業務の委託）
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    弊社は、本サービスの運営に必要な範囲で、個人情報の取扱いを外部に委託することがあります。この場合、弊社は、個人情報を適切に取り扱っていると認められる委託先を選定し、委託契約等において個人情報の適切な管理を求め、適切な監督を行います。
                  </Typography>
                }
              />
            </ListItem>

            {/* 第12条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第１２条（外部サービスの利用）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      本サービスでは、以下の外部サービスを利用しています。これらのサービスにおける個人情報の取扱いについては、各サービスのプライバシーポリシーをご確認ください。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （1）OpenAI（ChatGPT API）：AIチャット機能の提供
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1, pl: 2 }}>
                      （2）Google Analytics：アクセス解析
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, pl: 2 }}>
                      （3）その他、本サービスの提供に必要な外部サービス
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第13条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第１３条（個人情報の取扱いに関する相談・苦情）
                  </Typography>
                }
                secondary={
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    弊社における個人情報の取扱いに関するご質問、ご相談、苦情等がございましたら、下記のお問い合わせ窓口までご連絡ください。弊社は、誠実かつ迅速に対応させていただきます。
                  </Typography>
                }
              />
            </ListItem>

            {/* 第14条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第１４条（プライバシーポリシーの変更）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      １　弊社は、法令の改正、本サービスの変更等に伴い、本ポリシーを変更することがあります。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      ２　本ポリシーを変更する場合、弊社は、変更後の本ポリシーの施行時期及び内容を弊社ウェブサイト上での掲示その他の適切な方法により周知し、又は学生ユーザーに通知します。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      ３　変更後の本ポリシーは、第1項に定める施行時期から効力を生じるものとします。
                    </Typography>
                  </Box>
                }
              />
            </ListItem>

            {/* 第15条 */}
            <ListItem sx={{ alignItems: 'flex-start', px: 0 }}>
              <ListItemText
                primary={
                  <Typography variant="h6" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
                    第１５条（個人情報保護管理者）
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 1 }}>
                      弊社は、個人情報保護管理者を設置し、個人情報の適切な管理を行います。
                    </Typography>
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      個人情報保護管理者：𠮷岡 歩真（代表取締役）
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          </List>

          <Divider sx={{ mb: 4 }} />

          {/* Contact Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component="div" sx={{ mb: 2, fontWeight: 'bold' }}>
              個人情報に関するお問い合わせ窓口
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
              個人情報の開示、訂正、削除、利用停止等のご請求、その他個人情報の取扱いに関するお問い合わせは、以下の窓口までご連絡ください。
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              株式会社Gerbera　個人情報お問い合わせ窓口
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              電話番号：06-7176-5480
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              FAX番号：06-7635-8254
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              E-mail：info@gakufudo-career.jp
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              受付時間：平日 10:00～18:00（土日祝日、年末年始を除く）
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Effective Date */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              制定日：2025年9月30日
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              最終改定日：2025年9月30日
            </Typography>
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary">
              以上
            </Typography>
          </Box>
        </Paper>

        {/* Bottom Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography 
            variant="body2" 
            color="white" 
            sx={{ opacity: 0.8 }}
          >
            © {new Date().getFullYear()} 株式会社Gerbera. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyStudentPage;